<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { onKeyStroke } from "@vueuse/core";
  import { onMounted, onUnmounted } from "vue";

  import IconHint from "./icon-hint.vue";
  import ServingSizeCalculator from "./serving-size-calculator.vue";

  const TITLE_HINT = `This widget is based on the FDA
<a href="https://www.ecfr.gov/current/title-21/chapter-I/subchapter-B/part-101/subpart-A/section-101.12"
target="_blank" rel="noopener">serving size</a> and
<a href="https://www.ecfr.gov/current/title-21/part-101/section-101.9#p-101.9(b)" target="_blank" rel="noopener">nutrition labeling</a> regulations.`;

  const props = defineProps({
    suggestedServingSelector: { type: String, required: true },
    servingsSelector: { type: String, required: true },
    servingSizeSelector: { type: String, required: true },
    fdaCategorySelector: { type: String, required: true },
    visualUnitOfMeasureSelector: { type: String, required: true },
    visualUnitOfMeasure: { type: String, default: "" },
    perPackageWeight: { type: Number, required: true },
    yieldMethod: { type: String, required: true },
    displayUnit: { type: String, required: true },
    unitRatio: { type: Number, required: true },
    inputPrecision: { type: Number, required: true },
    categoryId: { type: Number, default: null },
  });

  let isOpen = _ref(false);
  let serving = _ref(null);

  const open = () => {
    isOpen.value = true;
  };
  const close = () => {
    isOpen.value = false;
    serving.value = null;
  };

  const onServingChange = (data) => {
    serving.value = data;
  };

  const connectedField = _computed(() => {
    const value = serving.value?.connectedFieldValue;
    if (value == null) return null;

    if (props.yieldMethod === "package_size_and_count") {
      return { selector: props.servingsSelector, name: "servings", value };
    } else if (props.yieldMethod === "ingredient_weight") {
      const valueInUnit = Number((value / props.unitRatio).toFixed(props.inputPrecision));
      return {
        selector: props.servingSizeSelector,
        name: "serving size weight",
        value: valueInUnit,
      };
    }
  });

  const useServingSize = (updateConnectedFields) => {
    const suggestedServingEl = document.querySelector(props.suggestedServingSelector);
    suggestedServingEl.focus();
    suggestedServingEl.value = serving.value.servingSize.serving;
    if (updateConnectedFields && connectedField.value) {
      const el = document.querySelector(connectedField.value.selector);
      if (el) el.value = connectedField.value.value;
    }
    const fdaCategoryEl = document.querySelector(props.fdaCategorySelector);
    if (fdaCategoryEl) fdaCategoryEl.value = serving.value.categoryId || "";
    const visualUnitEl = document.querySelector(props.visualUnitOfMeasureSelector);
    visualUnitEl.disabled = false;
    visualUnitEl.value = serving.value.servingSize.visualUnit ?? "";
    suggestedServingEl.dispatchEvent(new Event("change", { bubbles: true }));
    close();
  };

  onKeyStroke("Escape", close);
  onMounted(() => document.addEventListener("open-serving-size-calculator", open));
  onUnmounted(() => document.removeEventListener("open-serving-size-calculator", open));
</script>

<template>
  <div class="modal" :class="{ 'is-active': isOpen }">
    <div class="modal-background" @click="close"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <div class="modal-card-title is-flex gap-2">
          <span>Find Serving Size</span>
          <IconHint :content="TITLE_HINT" :interactive="true" />
        </div>
        <button class="delete" aria-label="Close modal" @click="close"></button>
      </header>
      <section class="modal-card-body">
        <ServingSizeCalculator
          v-if="isOpen"
          :per-package-weight="props.perPackageWeight"
          :display-unit="displayUnit"
          :yield-method="props.yieldMethod"
          :category-id="props.categoryId"
          :visual-unit-of-measure="props.visualUnitOfMeasure"
          @fetch-error="close"
          @serving-change="onServingChange"
        />
      </section>
      <footer class="modal-card-foot">
        <template v-if="serving?.servingSize">
          <button v-if="connectedField" class="button is-primary" @click="useServingSize(true)">
            Set suggested serving and {{ connectedField.name }}
          </button>
          <button
            class="button"
            :class="{ 'is-primary': !connectedField }"
            @click="useServingSize(false)"
          >
            Set suggested serving
          </button>
        </template>
      </footer>
    </div>
  </div>
</template>
